
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    MdAdd,
    MdEdit,
    MdDelete,
    MdMoreVert,
    MdVisibility,
    MdSearch
} from 'react-icons/md';
import './ProjectCategory.css'; // Reusing styles
import '../pages/Dashboard.css';
import Swal from 'sweetalert2';
import axios from 'axios';
import {
    GET_ALL_PROJECT_SUB_CATEGORY_DETAILS_API,
    DELETE_PROJECT_SUB_CATEGORY_DETAIL_API
} from '../config/API';
import Loader from '../components/common/Loader';
import NoData from '../components/common/NoData';
import Pagination from '../components/common/Pagination';
import TableLimitSelector from '../components/common/TableLimitSelector';
import ActionMenuPortal from '../components/common/ActionMenuPortal';
import { useUser } from '../context/UserContext';

const ProjectSubCategoryDetail = () => {
    const navigate = useNavigate();
    const { hasPermission } = useUser();
    const [details, setDetails] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [openMenuId, setOpenMenuId] = useState(null);
    const [anchorRect, setAnchorRect] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    // Debounce search input
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Reset to page 1 when search changes
    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearch]);

    const fetchDetails = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('accessToken');
            const response = await axios.get(GET_ALL_PROJECT_SUB_CATEGORY_DETAILS_API, {
                params: {
                    page: currentPage,
                    limit: limit,
                    search: debouncedSearch || undefined
                },
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.status) {
                setDetails(response.data.data || []);
                const pagination = response.data.pagination || {};
                setTotalPages(pagination.totalPages || response.data.totalPages || 1);
                setTotalRecords(pagination.total || pagination.totalRecords || response.data.totalRecords || 0);
            }
        } catch (error) {
            console.error('Error fetching details:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDetails();
    }, [currentPage, debouncedSearch, limit]);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleLimitChange = (newLimit) => {
        setLimit(newLimit);
        setCurrentPage(1);
    };

    const toggleMenu = (id, e) => {
        e.stopPropagation();
        if (openMenuId === id) {
            setOpenMenuId(null);
            setAnchorRect(null);
        } else {
            setOpenMenuId(id);
            setAnchorRect(e.currentTarget.getBoundingClientRect());
        }
    };

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (event.target.closest('.swal2-container')) return;
            if (event.target.closest('.action-portal-wrapper')) return;
            if (event.target.closest('.action-menu-btn')) return;

            setOpenMenuId(null);
            setAnchorRect(null);
        };

        document.addEventListener('click', handleClickOutside, true);
        return () => {
            document.removeEventListener('click', handleClickOutside, true);
        };
    }, []);

    const handleDelete = async (id) => {
        setOpenMenuId(null);
        const result = await Swal.fire({
            title: 'Delete Detail?',
            text: 'Are you sure you want to delete this detail?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, Delete',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#dc2626',
            buttonsStyling: false,
            customClass: {
                confirmButton: 'swal-delete-btn',
                cancelButton: 'swal-cancel-btn',
            },
        });

        if (result.isConfirmed) {
            try {
                const token = localStorage.getItem('accessToken');
                const response = await axios.delete(DELETE_PROJECT_SUB_CATEGORY_DETAIL_API(id), {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (response.data.status) {
                    Swal.fire({
                        icon: 'success',
                        title: 'Deleted!',
                        text: 'Detail has been deleted.',
                        timer: 1500,
                        showConfirmButton: false
                    });
                    
                    // Optimistic update
                    setDetails(prev => prev.filter(item => (item._id || item.id) !== id));
                    
                    // If it was the last item on the page and not on page 1, go to previous page
                    if (details.length === 1 && currentPage > 1) {
                        setCurrentPage(prev => prev - 1);
                    } else {
                        fetchDetails();
                    }
                } else {
                    Swal.fire('Error', response.data.error || response.data.message || 'Failed to delete detail', 'error');
                }
            } catch (error) {
                console.error('Error deleting detail:', error);
                Swal.fire('Error', error.response?.data?.error || error.response?.data?.message || 'An error occurred while deleting', 'error');
            }
        }
    };

    return (
        <div className="dashboard">
            <div className="category-header">
                <h2 className="section-title" style={{ margin: 0 }}>Sub-Category Details</h2>
                {hasPermission('PROJECT_SUB_CATEGORY_DETAILS', 'create') && (
                    <button className="add-category-btn" onClick={() => navigate('/project-sub-category-details/new')}>
                        <MdAdd size={20} />
                        Add Detail
                    </button>
                )}
            </div>

            <div className="card-container-category">
                {/* Search Bar */}
                <div style={{ padding: '1rem', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
                    <div style={{ position: 'relative', maxWidth: '500px' }}>
                        <MdSearch
                            style={{
                                position: 'absolute',
                                left: '12px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                color: '#9ca3af',
                                fontSize: '1.25rem'
                            }}
                        />
                        <input
                            type="text"
                            placeholder="Search details..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '0.625rem 1rem 0.625rem 2.5rem',
                                border: '1px solid #d1d5db',
                                borderRadius: '8px',
                                fontSize: '0.875rem',
                                outline: 'none',
                                transition: 'border-color 0.2s',
                                background: 'white'
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#4f46e5'}
                            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                        />
                        {searchTerm && (
                            <button
                                onClick={() => setSearchTerm('')}
                                style={{
                                    position: 'absolute',
                                    right: '10px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    background: '#e5e7eb',
                                    border: 'none',
                                    borderRadius: '50%',
                                    width: '20px',
                                    height: '20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    color: '#4b5563',
                                    fontSize: '12px'
                                }}
                            >
                                ✕
                            </button>
                        )}
                    </div>
                </div>
                {loading ? (
                    <Loader text="Fetching details..." />
                ) : details.length === 0 ? (
                    <NoData text="No details found. Click 'Add Detail' to create one." />
                ) : (
                    <>
                        <div className="table-container">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Detail Name</th>
                                        <th>Status</th>
                                        <th style={{ textAlign: 'right' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {details.map((item) => (
                                        <tr key={item._id || item.id}>
                                            <td>
                                                <strong>{item.detailName}</strong>
                                            </td>

                                            <td>
                                                <span
                                                    className={`status-badge ${item.status === 'active' ? 'active' : 'inactive'}`}
                                                >
                                                    {item.status}
                                                </span>
                                            </td>
                                            <td className={`action-cell ${openMenuId === (item._id || item.id) ? 'menu-open' : ''}`}>
                                                <button
                                                    className="action-menu-btn"
                                                    onClick={(e) => toggleMenu(item._id || item.id, e)}
                                                    title="Actions"
                                                >
                                                    <MdMoreVert size={20} />
                                                </button>

                                                <ActionMenuPortal
                                                    isOpen={openMenuId === (item._id || item.id)}
                                                    anchorRect={anchorRect}
                                                    onClose={() => setOpenMenuId(null)}
                                                >
                                                    <div className="action-dropdown">
                                                        {hasPermission('PROJECT_SUB_CATEGORY_DETAILS', 'read') && (
                                                            <button className="action-item" onClick={() => navigate(`/project-sub-category-details/view/${item._id || item.id}`)}>
                                                                <MdVisibility size={16} /> View Details
                                                            </button>
                                                        )}
                                                        {hasPermission('PROJECT_SUB_CATEGORY_DETAILS', 'update') && (
                                                            <button className="action-item" onClick={() => navigate(`/project-sub-category-details/edit/${item._id || item.id}`)}>
                                                                <MdEdit size={16} /> Edit
                                                            </button>
                                                        )}
                                                        {hasPermission('PROJECT_SUB_CATEGORY_DETAILS', 'delete') && (
                                                            <button
                                                                className="action-item delete"
                                                                onClick={() => handleDelete(item._id || item.id)}
                                                            >
                                                                <MdDelete size={16} /> Delete
                                                            </button>
                                                        )}
                                                    </div>
                                                </ActionMenuPortal>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile Card View */}
                        <div className="mobile-card-view">
                            {details.map((item) => (
                                <div key={item._id || item.id} className="category-card">
                                    <div className="card-header">
                                        <div className="card-title">
                                            <strong>{item.detailName}</strong>
                                        </div>
                                        <button
                                            className="action-menu-btn"
                                            onClick={(e) => toggleMenu(item._id || item.id, e)}
                                            title="Actions"
                                        >
                                            <MdMoreVert size={20} />
                                        </button>
                                        <ActionMenuPortal
                                            isOpen={openMenuId === (item._id || item.id)}
                                            anchorRect={anchorRect}
                                            onClose={() => setOpenMenuId(null)}
                                        >
                                            <div className="action-dropdown shadow-lg">
                                                {hasPermission('PROJECT_SUB_CATEGORY_DETAILS', 'read') && (
                                                    <button className="action-item" onClick={() => navigate(`/project-sub-category-details/view/${item._id || item.id}`)}>
                                                        <MdVisibility size={16} /> View Details
                                                    </button>
                                                )}
                                                {hasPermission('PROJECT_SUB_CATEGORY_DETAILS', 'update') && (
                                                    <button className="action-item" onClick={() => navigate(`/project-sub-category-details/edit/${item._id || item.id}`)}>
                                                        <MdEdit size={16} /> Edit
                                                    </button>
                                                )}
                                                {hasPermission('PROJECT_SUB_CATEGORY_DETAILS', 'delete') && (
                                                    <button
                                                        className="action-item delete"
                                                        onClick={() => handleDelete(item._id || item.id)}
                                                    >
                                                        <MdDelete size={16} /> Delete
                                                    </button>
                                                )}
                                            </div>
                                        </ActionMenuPortal>
                                    </div>
                                    <div className="card-body">

                                        <div className="card-row">
                                            <span className="card-label">Status:</span>
                                            <span
                                                className={`status-badge ${item.status === 'active' ? 'active' : 'inactive'}`}
                                            >
                                                {item.status}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={handlePageChange}
                            totalRecords={totalRecords}
                            currentCount={details.length}
                            entityName="Sub-Category Details"
                         limit={limit} onLimitChange={handleLimitChange} />
                    </>
                )}
            </div>
        </div>
    );
};

export default ProjectSubCategoryDetail;
